import { useTranslation } from "react-i18next";
import {
  Button,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  SearchInput,
} from "@tellescope/ui";
import { PaginatedList } from "@tellescope/design-system";
import { useExistingFilePicker } from "./useExistingFilePicker";
import { ExistingFilePickerRow } from "./ExistingFilePickerRow";
import type { UploadedValueItem } from "./useFileFieldLogic";

export interface ExistingFilePickerDialogContentsProps {
  enduserId: string;
  excludedSecureNames?: string[];
  validFileTypes?: string[];
  multiple: boolean;
  onSelect: (files: UploadedValueItem[]) => void;
  onCancel: () => void;
  isOpen: boolean;
}

export const ExistingFilePickerDialogContents = ({
  enduserId,
  excludedSecureNames,
  validFileTypes,
  multiple,
  onSelect,
  onCancel,
  isOpen,
}: ExistingFilePickerDialogContentsProps) => {
  const { t } = useTranslation("forms");
  const picker = useExistingFilePicker({
    enduserId,
    excludedSecureNames,
    validFileTypes,
    multiple,
    enableQuery: isOpen,
  });

  return (
    <DialogContent className="sm:max-w-lg max-w-[calc(100%-2rem)] max-h-[80vh] flex flex-col" divided>
      <DialogHeader className="px-6 pt-6">
        <DialogTitle>{multiple ? t("Select existing files") : t("Select an existing file")}</DialogTitle>
        <DialogDescription>
          {multiple
            ? t("Choose one or more files already uploaded for this contact.")
            : t("Choose a file already uploaded for this contact.")}
        </DialogDescription>
      </DialogHeader>

      <div className="px-6 py-3">
        <SearchInput
          value={picker.searchQuery}
          onChange={(e) => picker.setSearchQuery(e.target.value)}
          placeholder={t("Search files...")}
          className="w-full"
        />
      </div>

      {picker.isError ? (
        <div className="flex items-center justify-center h-32 px-6">
          <p className="text-sm text-destructive">{t("Error loading files")}</p>
        </div>
      ) : (
        <PaginatedList
          isLoading={picker.isLoading || picker.isSearchPending}
          loadingMessage={t("Loading files...")}
          onBottomReached={picker.fetchNextPage}
          className="flex-1 min-h-0 px-6"
          scrollOptions={{ type: "hidden-until-scroll" }}
        >
          {picker.files?.length
            ? picker.files.map((file) => (
                <ExistingFilePickerRow
                  key={file.secureName}
                  name={file.name}
                  secureName={file.secureName}
                  type={file.type}
                  size={file.size}
                  timestamp={file.timestamp}
                  isSelected={picker.selectedSecureNames.includes(file.secureName)}
                  onSelect={() => picker.toggleSelected(file.secureName)}
                />
              ))
            : [
                <p key="empty" className="text-sm text-muted-foreground text-center py-8">
                  {t("No files found for this contact")}
                </p>,
              ]}
        </PaginatedList>
      )}

      <DialogFooter className="px-6 pb-6">
        <Button variant="outline" onClick={onCancel}>
          {t("Cancel")}
        </Button>
        <Button
          disabled={picker.selectedFiles.length === 0}
          onClick={() =>
            onSelect(picker.selectedFiles.map(({ name, secureName, type }) => ({ name, secureName, type })))
          }
        >
          {multiple ? t("Select {{count}} file", { count: picker.selectedFiles.length }) : t("Select file")}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};
